import type { ReactNode } from "react";
import { cn } from "@newxsystems/shared";
import type { BlockAction, BlockSpacing, RequiredBlockLink } from "@newxsystems/types";
import { resolveKaajPrimitive } from "../../../client";
import { getKaajSectionSpacing } from "../../../modules/layout/kaaj-section-spacing";
import { RichTextHtml } from "../RichTextHtml";

export const KaajSectionShell = ({
  children,
  className,
  containerClassName,
  id,
  spacing,
}: {
  children: ReactNode;
  className?: string;
  containerClassName?: string;
  id?: string;
  spacing?: BlockSpacing | null;
}) => (
  <section
    id={id}
    className={cn(
      "relative overflow-hidden text-[#151927]",
      getKaajSectionSpacing(spacing),
      className,
    )}
  >
    <div className={cn("kaaj-container mx-auto px-4 sm:px-6 lg:px-8", containerClassName)}>
      {children}
    </div>
  </section>
);

export const KaajCard = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div
    className={cn(
      "rounded-[28px] border border-[#D9E1FF] bg-white/90 shadow-[0_24px_80px_rgba(33,68,255,0.10)] backdrop-blur",
      className,
    )}
  >
    {children}
  </div>
);

export const KaajHeading = ({
  align,
  eyebrow,
  heading,
  inverse = false,
  subheading,
}: {
  align?: "left" | "center";
  eyebrow?: string;
  heading?: string;
  inverse?: boolean;
  subheading?: string;
}) => {
  if (!eyebrow && !heading && !subheading) return null;

  return (
    <header className={cn("mb-8 max-w-3xl lg:mb-10", align === "center" && "mx-auto text-center")}>
      {eyebrow && (
        <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#2144FF]">
          {eyebrow}
        </p>
      )}
      {heading && (
        <h2 className={cn("kaaj-section-heading", inverse ? "text-white" : "text-[#151927]")}>
          {heading}
        </h2>
      )}
      {subheading && (
        <RichTextHtml
          className={cn(
            "mt-4 max-w-2xl text-lg leading-8",
            align === "center" && "mx-auto",
            inverse ? "text-[#D9E1FF]" : "text-[#4B5568]",
          )}
          html={subheading}
        />
      )}
    </header>
  );
};

export const KaajBadge = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex items-center rounded-full border border-[#D9E1FF] bg-[#F6F8FF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#2144FF]">
    {children}
  </span>
);

export const KaajActions = ({
  actions,
  className,
}: {
  actions: Array<BlockAction | RequiredBlockLink>;
  className?: string;
}) => {
  const Link = resolveKaajPrimitive("Link");

  if (actions.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap gap-3", className)}>
      {actions.map((action, index) => {
        const style = "style" in action ? action.style : "primary";
        const isPrimary = style === "primary";
        const rel = action.target === "_blank" ? "noopener noreferrer" : undefined;

        return (
          <Link
            key={`${action.href}-${action.text}-${index}`}
            aria-label={action.ariaLabel}
            className={cn(
              "inline-flex min-h-11 items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-2",
              isPrimary
                ? "bg-[#2144FF] text-white shadow-[0_12px_30px_rgba(33,68,255,0.28)] hover:bg-[#1735D1]"
                : "border border-[#D9E1FF] bg-white text-[#2144FF] hover:border-[#2144FF] hover:bg-[#F6F8FF]",
            )}
            href={action.href}
            rel={rel}
            target={action.target}
          >
            {action.text}
          </Link>
        );
      })}
    </div>
  );
};
